import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Observable, of } from 'rxjs';
import {
  ConnectionChangeDto,
  ConnectionChangeRequestTypeDisplayValues,
  ConnectionChangeRequestTypeEnum
} from '@bo-client/shared/models/connectionChange.dto';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { OrderRequestWizardService } from '@bo-client/nap/order-request-wizard/order-request-wizard.service';
import { articles } from '@bo-client/shared/constants/articles';
import { path } from '@bo-client/shared/constants/path';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-order-request-wizard-feedback',
  templateUrl: './order-request-wizard-feedback.component.html',
  styleUrl: './order-request-wizard-feedback.component.scss'
})
export class OrderRequestWizardFeedbackComponent implements OnInit {
  model$: Observable<ConnectionChangeDto | null> = of(null);
  info$: Observable<string | null> = of(null);
  token$: Observable<string> = of(null);

  #destroyRef = inject(DestroyRef);
  #orderRequestWizardService = inject(OrderRequestWizardService);
  #toastrService = inject(ToastrService);
  #router = inject(Router);

  constructor(private route: ActivatedRoute, private orderRequestWizardService: OrderRequestWizardService) {}
  ngOnInit() {
    this.token$ = this.route.paramMap.pipe(
      takeUntilDestroyed(this.#destroyRef),
      map((params) => {
        if (!params.has('token')) {
          return null;
        }
        return params.get('token');
      }),
      shareReplay(1)
    );
    this.model$ = this.route.paramMap.pipe(
      takeUntilDestroyed(this.#destroyRef),
      switchMap((params) => {
        if (!params.has('id') || !params.has('token')) {
          return of(null);
        }
        return this.orderRequestWizardService.getEntityAsExternal(params.get('id'), params.get('token')).pipe(
          catchError((error) => {
            console.error(
              'Error fetching feedback entity with id ' +
                params.get('id') +
                ' and token ' +
                params.get('token') +
                ':',
              error
            );
            return of(null);
          }),
          map((response) => {
            if (response) {
              return response.entity;
            }
            return null;
          })
        );
      }),
      shareReplay(1)
    );
    this.info$ = this.model$.pipe(
      map((model) => {
        if (model && model.requestFeedbackComment) {
          return model.requestFeedbackComment;
        }
        return null;
      }),
      shareReplay(1)
    );
  }

  submitFeedback(dto: ConnectionChangeDto, token: string) {
    if (!dto || !token) {
      this.#toastrService.error(
        'Im Formular wurde ein Fehler festgestellt, bitte überprüfen Sie Ihre Eingaben auf Korrektheit',
        'Fehler beim Absenden des Formulars'
      );
      return;
    }
    this.#orderRequestWizardService.submitFeedback(dto, token).subscribe({
      next: () => {
        const subjectType = ConnectionChangeRequestTypeDisplayValues[dto.connectionChangeRequestType];
        const subject = `${articles.upper.formal[subjectType?.gender].acc} ${
          dto.connectionChangeRequestType === ConnectionChangeRequestTypeEnum.TEMPORARY
            ? 'temporären Anschluss'
            : subjectType?.name
        }`;

        this.#toastrService.success(
          'Vielen Dank, wir haben Ihre Anfrage erhalten.',
          'Rückmeldung erfolgreich gesendet'
        );
        const message = `Wir haben Ihre Rückmeldung für ${subject} erhalten. Sie werden in Kürze eine Bestätigungsmail mit allen Informationen zu Ihrer erneuten Anfrage erhalten.`;
        this.#router
          .navigate([path.external + '/' + path.goToThanksPage + '/' + message], {
            replaceUrl: true
          })
          .then();
      },
      error: (err) => {
        this.#toastrService.error(
          'Beim Übermitteln ist ein Fehler aufgetreten: ' + err.status + ' ' + err.error
        );
      }
    });
  }
}
